import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  ShoppingBag,
  Package,
  TrendingUp,
  Info,
  MessageSquare,
  Receipt,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { HepsiburadaCredentials } from '../services/hepsiburadaApiService';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';

interface HepsiburadaConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (credentials: HepsiburadaCredentials, connectionId: string) => void;
}

export function HepsiburadaConnectModal({
  isOpen,
  onClose,
  onSuccess,
}: HepsiburadaConnectModalProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const { user } = useAuth();
  const { checkDuplicateApiKey } = useSubscription();

  const [merchantId, setMerchantId] = useState('');
  const [serviceKey, setServiceKey] = useState('');
  const [integratorName, setIntegratorName] = useState('');
  const [showServiceKey, setShowServiceKey] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'validating' | 'success'>('form');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!merchantId.trim() || !serviceKey.trim() || !integratorName.trim()) {
      setError(isEnglish ? 'All fields are required' : 'Tum alanlar zorunludur');
      return;
    }

    if (!user?.uid) {
      setError(isEnglish ? 'Please login first' : 'Lutfen once giris yapin');
      return;
    }

    setIsValidating(true);
    setStep('validating');

    try {
      // Check for duplicate API key (mukerrer hesap kontrolu)
      const duplicateCheck = await checkDuplicateApiKey(merchantId.trim(), 'hepsiburada');
      if (duplicateCheck.isDuplicate) {
        setStep('form');
        setError(duplicateCheck.message || (isEnglish
          ? 'Duplicate account detected! This Merchant ID is already used in another account. Please check your main account or contact support.'
          : 'Mukerrer hesap tespit edildi! Bu Merchant ID baska bir hesapta kullanilmaktadir. Lutfen ana hesabinizi kontrol edin veya destek ile iletisime gecin.'));
        setIsValidating(false);
        return;
      }

      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';

      // Save connection via Cloud Function (validates and saves to Firestore)
      const response = await fetch(`${FUNCTIONS_URL}/saveHepsiburadaConnection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          merchantId: merchantId.trim(),
          serviceKey: serviceKey.trim(),
          integratorName: integratorName.trim(),
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setStep('form');
        setError(result.error || (isEnglish ? 'Invalid credentials' : 'Gecersiz kimlik bilgileri'));
        setIsValidating(false);
        return;
      }

      // Success!
      const credentials: HepsiburadaCredentials = {
        merchantId: merchantId.trim(),
        username: integratorName.trim(), // For compatibility
        password: serviceKey.trim(), // For compatibility
      };

      setStep('success');
      setTimeout(() => {
        onSuccess(credentials, result.connectionId);
        handleClose();
      }, 1500);
    } catch (err) {
      setStep('form');
      setError(
        err instanceof Error
          ? err.message
          : isEnglish
          ? 'Connection failed. Please try again.'
          : 'Baglanti basarisiz. Lutfen tekrar deneyin.'
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleClose = () => {
    setMerchantId('');
    setServiceKey('');
    setIntegratorName('');
    setShowServiceKey(false);
    setError(null);
    setStep('form');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-surface rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-border-default">
        {/* Header with Hepsiburada Branding */}
        <div
          className="relative p-6 pb-8"
          style={{
            background: 'linear-gradient(135deg, #FF6000 0%, #FF8534 100%)',
          }}
        >
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <X size={18} />
          </button>

          <div className="flex items-center gap-4">
            {/* Hepsiburada Logo */}
            <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center shadow-lg">
              <svg viewBox="0 0 32 32" className="w-10 h-10">
                <rect fill="#FF6000" width="32" height="32" rx="6" />
                <text
                  x="16"
                  y="21"
                  textAnchor="middle"
                  fill="white"
                  fontSize="16"
                  fontWeight="bold"
                  fontFamily="Arial"
                >
                  hb
                </text>
              </svg>
            </div>
            <div className="text-white">
              <h2 className="text-xl font-bold">Hepsiburada</h2>
              <p className="text-white/80 text-sm">
                {isEnglish ? 'Connect your merchant account' : 'Satici hesabinizi baglayin'}
              </p>
            </div>
          </div>

          {/* Features */}
          <div className="flex flex-wrap gap-3 mt-6">
            {[
              { icon: ShoppingBag, label: isEnglish ? 'Orders' : 'Siparisler' },
              { icon: Package, label: isEnglish ? 'Products' : 'Urunler' },
              { icon: TrendingUp, label: isEnglish ? 'Analytics' : 'Analitik' },
              { icon: MessageSquare, label: isEnglish ? 'Q&A' : 'Sorular' },
              { icon: Receipt, label: isEnglish ? 'Accounting' : 'Muhasebe' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-white/90 text-sm">
                <Icon size={14} />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'validating' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-[#FF6000]/10 flex items-center justify-center mx-auto mb-4">
                <Loader2 size={32} className="text-[#FF6000] animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {isEnglish ? 'Validating credentials...' : 'Kimlik bilgileri dogrulanıyor...'}
              </h3>
              <p className="text-sm text-text-secondary">
                {isEnglish
                  ? 'Connecting to Hepsiburada API'
                  : 'Hepsiburada API\'sine baglaniliyor'}
              </p>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-green-500" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {isEnglish ? 'Successfully connected!' : 'Basariyla baglandi!'}
              </h3>
              <p className="text-sm text-text-secondary">
                {isEnglish
                  ? 'Your Hepsiburada account is now connected'
                  : 'Hepsiburada hesabiniz artik bagli'}
              </p>
            </div>
          )}

          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error Banner */}
              {error && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                  <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* Info Banner */}
              <div className="flex items-start gap-3 p-4 rounded-xl bg-[#FF6000]/10 border border-[#FF6000]/30">
                <Info size={20} className="text-[#FF6000] flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-[#FF6000] font-medium mb-1">
                    {isEnglish ? 'Where to find your API credentials?' : 'API bilgilerinizi nereden bulabilirsiniz?'}
                  </p>
                  <p className="text-text-secondary">
                    {isEnglish
                      ? 'Go to Hepsiburada Merchant Panel → Account Settings → API Information'
                      : 'Hepsiburada Satici Paneli → Hesap Ayarlari → API Bilgileri'}
                  </p>
                </div>
              </div>

              {/* Merchant ID */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Magaza ID (Merchant ID)
                </label>
                <input
                  type="text"
                  value={merchantId}
                  onChange={(e) => setMerchantId(e.target.value)}
                  placeholder="6bf28cdc-d933-46c6-aeb1-77ba6f40c5d2"
                  className="w-full px-4 py-3 rounded-xl bg-bg-elevated border border-border-default focus:border-[#FF6000] focus:outline-none focus:ring-2 focus:ring-[#FF6000]/20 text-text-primary placeholder:text-text-tertiary transition-all font-mono text-sm"
                />
              </div>

              {/* Service Key */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Servis Anahtari (Service Key)
                </label>
                <div className="relative">
                  <input
                    type={showServiceKey ? 'text' : 'password'}
                    value={serviceKey}
                    onChange={(e) => setServiceKey(e.target.value)}
                    placeholder="GPEFf4NMA75Z"
                    className="w-full px-4 py-3 pr-12 rounded-xl bg-bg-elevated border border-border-default focus:border-[#FF6000] focus:outline-none focus:ring-2 focus:ring-[#FF6000]/20 text-text-primary placeholder:text-text-tertiary transition-all font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowServiceKey(!showServiceKey)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary transition-colors"
                  >
                    {showServiceKey ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Integrator Name */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Entegrator Adi (User-Agent)
                </label>
                <input
                  type="text"
                  value={integratorName}
                  onChange={(e) => setIntegratorName(e.target.value)}
                  placeholder="firmaadi_dev"
                  className="w-full px-4 py-3 rounded-xl bg-bg-elevated border border-border-default focus:border-[#FF6000] focus:outline-none focus:ring-2 focus:ring-[#FF6000]/20 text-text-primary placeholder:text-text-tertiary transition-all"
                />
                <p className="text-xs text-text-tertiary mt-1">
                  {isEnglish
                    ? 'The integrator name you selected when getting the service key'
                    : 'Servis anahtarini alirken sectiginiz entegrator adi'}
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 rounded-xl bg-bg-elevated border border-border-default text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-all font-medium"
                >
                  {isEnglish ? 'Cancel' : 'Iptal'}
                </button>
                <button
                  type="submit"
                  disabled={isValidating}
                  className={cn(
                    'flex-1 px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2',
                    'bg-[#FF6000] text-white hover:bg-[#E55500]',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {isValidating ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {isEnglish ? 'Connecting...' : 'Baglaniyor...'}
                    </>
                  ) : (
                    isEnglish ? 'Connect' : 'Baglan'
                  )}
                </button>
              </div>

              {/* Help Link */}
              <div className="text-center pt-2">
                <a
                  href="https://developers.hepsiburada.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-text-tertiary hover:text-[#FF6000] transition-colors"
                >
                  <ExternalLink size={14} />
                  {isEnglish ? 'API Documentation' : 'API Dokumantasyonu'}
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
